import { HTTP_INTERCEPTORS, HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, filter, finalize, switchMap, take } from 'rxjs/operators';
import { AuthService } from './core/services/authentication.service';
import { RouteUrlConstants } from './models/route-url-constants';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import { LoaderService } from './services/loader.service';
import { AppCookieService } from '@ace/ace';

const TOKEN_HEADER_KEY = 'authorization';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  isRefreshTokenSent: boolean;
  isRefreshTokenFetched: boolean;
  private refreshTokenSubject: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);
  constructor(
    private cookieService: AppCookieService,
    private authService: AuthService,
    private route: Router,
    private loader: LoaderService,
    public toastr: ToastrService,
    public translate: TranslateService) { }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    this.loader.showLoader()
    let authReq = req;
    const skipAuthHeaders = req.headers.get('skipAuthHeaders');
    const skipUnauthorizedRedirect = req.headers.get('skipUnauthorizedRedirect');
    req = req.clone({ headers: req.headers.delete('skipAuthHeaders') });
    req = req.clone({ headers: req.headers.delete('skipUnauthorizedRedirect') });
    if (!skipAuthHeaders) {
      const token = this.cookieService.getCookie("accessToken");
      if (token) {
        if (token !== null) {
          authReq = this.addTokenHeader(req, token);
        }
      }
    }

    return this.handleCall(req, next, authReq, !!skipUnauthorizedRedirect);
  }

  private handleCall(
    req: HttpRequest<any>,
    next: HttpHandler,
    authReq: HttpRequest<any>,
    skipUnauthorizedRedirect = false,
  ): Observable<HttpEvent<any>> {
    return next.handle(authReq).pipe(catchError(error => {
      if (
        !skipUnauthorizedRedirect &&
        (error?.error?.httpStatus === 'UNAUTHORIZED' || (error instanceof HttpErrorResponse && !authReq.url.includes('auth') && error.status === 401))
      ) {
        return this.handle401Error(authReq, next);
      } else if (error.status === 403) {
        this.route.navigateByUrl(RouteUrlConstants.FORBIDDEN);
      } else if (error.status === 500) {
        this.toastr.error('Something went wrong .....')
      }
      return throwError(error);
    }),
      finalize(() => {
        this.loader.hideLoader()
      })
    );
  }

  private handle401Error(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const refreshToken = this.cookieService.getCookie("refreshToken");
    if (!this.isRefreshTokenFetched) {
      if (refreshToken) {
        this.clearCookie();
        this.isRefreshTokenSent = true;
        this.isRefreshTokenFetched = true;
        return this.authService.refreshToken(refreshToken).pipe(
          take(1),
          switchMap((tokenRes: any) => {
            this.cookieService.setCookie("accessToken", tokenRes.content.accessToken, { path: '/' });
            this.cookieService.setCookie("refreshToken", refreshToken, { path: '/' });
            this.isRefreshTokenFetched = false;
            this.refreshTokenSubject.next(tokenRes.content.accessToken);
            return this.handleCall(req, next, this.addTokenHeader(req, tokenRes.content.accessToken));

          }),
          catchError((err) => {
            if (err?.error?.httpStatus === 'UNAUTHORIZED' || (err instanceof HttpErrorResponse && !req.url.includes('auth') && err.status === 401)) {
              this.isRefreshTokenFetched = false;
              this.clearCookie();
              this.logout();
            }
            return throwError(err);
          })
        );
      } else if (!this.isRefreshTokenSent) {
        this.clearCookie();
        this.isRefreshTokenFetched = false;
        this.logout();
        this.toastr.error(this.translate.instant("Your session has expired. Please log in again to continue"));
        return throwError("TOken is empty");
      }
    }
    else {
      // Queue requests while the token is being refreshed
      return this.refreshTokenSubject.pipe(
        filter((token) => token !== null), // Wait until the token is available
        take(1),
        switchMap((token) => {
          return this.handleCall(req, next, this.addTokenHeader(req, token));
        }) // Retry the request with the new token
      );
    }

    return throwError(() => new Error('Unhandled error scenario in handle401Error.'));

  }

  private logout() {
    this.route.navigateByUrl('login');
  }
  private clearCookie() {
    this.cookieService.deleteCookie("accessToken");
    this.cookieService.deleteCookie("refreshToken");
  }

  private addTokenHeader(request: HttpRequest<any>, token: string) {
    let headerToken: string;
    if (token) {
      if (token.startsWith("Bearer "))
        headerToken = token;
      else
        headerToken = "Bearer " + token;

      return request.clone(
        {
          setHeaders: {
            Authorization: `${headerToken}`
          }
        }
      );
    } else {
      return request.clone({ headers: request.headers.set(TOKEN_HEADER_KEY, "") });
    }
  }
}

export const authInterceptorProviders = [
  { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true }
];
